import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useTheme } from "../../context/ThemeContext";
import { getTheme } from "../../theme";
import MainLayout from "../../components/Layout/MainLayout";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import JobStatusBadge from "../../components/Jobs/JobStatusBadge";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from "../../components/common/Table";
import { api } from "../../services/api";
import {
  Refresh,
  Schedule,
  AccessTime,
} from "@mui/icons-material";

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
  margin: 0;
  ${(props) =>
    props.theme?.isDark &&
    `
    text-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
  `}
`;

const Actions = styled.div`
  display: flex;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  align-items: center;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(200px, 1fr)
  );
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
`;

const StatCard = styled(Card)`
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.textSecondary};
  text-transform: uppercase;
  font-weight: 500;
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.xs};
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
`;

const ScheduleTimeCard = styled(Card)`
  margin-bottom: ${(props) =>
    getTheme(props.theme?.isDark).spacing.lg};
`;

const ScheduleTimeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  border-bottom: 1px solid
    ${(props) =>
      getTheme(props.theme?.isDark).colors.border};
`;

const ScheduleTimeTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) =>
    getTheme(props.theme?.isDark).spacing.md};
  font-size: 18px;
  font-weight: 600;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.text};
`;

const NotificationBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${(props) =>
    getTheme(props.theme?.isDark).colors.primary}20;
  color: ${(props) =>
    getTheme(props.theme?.isDark).colors.primary};
`;

const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleString();
};

const ScheduledNotificationsView = () => {
  const { isDark } = useTheme();
  const theme = { isDark };
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [discoveryStatus, setDiscoveryStatus] =
    useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [
    showSimulatedTimeModal,
    setShowSimulatedTimeModal,
  ] = useState(false);
  const [selectedScheduleTime, setSelectedScheduleTime] =
    useState(null);
  const [simulatedTime, setSimulatedTime] = useState("");
  const [simulatedTimezone, setSimulatedTimezone] =
    useState("");
  const [running, setRunning] = useState(false);

  useEffect(() => {
    fetchData();
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [jobsResponse, statusResponse] =
        await Promise.all([
          api.getScheduledNotificationJobs(),
          api.getNotificationDiscoveryStatus(),
        ]);
      setScheduledJobs(jobsResponse.results || []);
      setDiscoveryStatus(statusResponse);
    } catch (err) {
      console.error(
        "Error fetching scheduled notifications:",
        err
      );
      setError(
        err.response?.data?.message ||
          err.message ||
          "Xəta baş verdi"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshJobs = async () => {
    if (
      !window.confirm(
        "Scheduled notification job-larını yeniləmək istəyirsiniz?"
      )
    ) {
      return;
    }
    try {
      setRefreshing(true);
      await api.refreshScheduledJobs();
      alert("Job-lar uğurla yeniləndi");
      fetchData();
    } catch (err) {
      console.error("Error refreshing jobs:", err);
      alert("Job-ları yeniləmək mümkün olmadı");
    } finally {
      setRefreshing(false);
    }
  };

  const handleRunWithSimulatedTime = (scheduleTime) => {
    setSelectedScheduleTime(scheduleTime);
    setSimulatedTime(scheduleTime); // Default olaraq schedule time-ı simulated time kimi istifadə et
    setSimulatedTimezone("");
    setShowSimulatedTimeModal(true);
  };

  const handleExecuteSimulatedTime = async () => {
    if (!simulatedTime) {
      alert("Simulated time təyin edilməlidir");
      return;
    }

    // Validate HH:mm format
    const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
    const isoDateTimeRegex =
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(\.\d{3})?(Z|[+-]\d{2}:\d{2})?$/;

    if (
      !timeRegex.test(simulatedTime) &&
      !isoDateTimeRegex.test(simulatedTime)
    ) {
      alert(
        "Simulated time formatı düzgün deyil. HH:mm (məs: 19:00) və ya ISO datetime formatı istifadə edin."
      );
      return;
    }

    try {
      setRunning(true);
      await api.runScheduledNotificationForTime({
        scheduleTime: selectedScheduleTime,
        simulatedTime: simulatedTime,
        timezone: simulatedTimezone || undefined,
      });
      alert(
        `Job ${selectedScheduleTime} üçün simulated time ${simulatedTime}${
          simulatedTimezone ? ` (${simulatedTimezone})` : ""
        } ilə uğurla işə salındı`
      );
      setShowSimulatedTimeModal(false);
      setSimulatedTime("");
      setSimulatedTimezone("");
      setSelectedScheduleTime(null);
      fetchData();
    } catch (err) {
      console.error(
        "Error running job with simulated time:",
        err
      );
      alert(
        err.response?.data?.message ||
          "Job-u simulated time ilə işə salmaq mümkün olmadı"
      );
    } finally {
      setRunning(false);
    }
  };

  return (
    <MainLayout>
      <PageContainer theme={theme}>
        <PageHeader>
          <Title theme={theme}>
            Scheduled Notifications
          </Title>
          <Actions>
            <Button
              onClick={handleRefreshJobs}
              disabled={refreshing}
              theme={theme}
            >
              <Refresh />{" "}
              {refreshing
                ? "Yenilənir..."
                : "Job-ları Yenilə"}
            </Button>
            <Button onClick={fetchData} theme={theme}>
              <Refresh /> Yenilə
            </Button>
          </Actions>
        </PageHeader>

        {discoveryStatus && (
          <StatsGrid theme={theme}>
            <StatCard theme={theme}>
              <StatLabel theme={theme}>
                Total Notifications
              </StatLabel>
              <StatValue theme={theme}>
                {
                  discoveryStatus.totalNotificationsWithScheduleTime
                }
              </StatValue>
            </StatCard>
            <StatCard theme={theme}>
              <StatLabel theme={theme}>
                Unique Schedule Times
              </StatLabel>
              <StatValue theme={theme}>
                {discoveryStatus.scheduleTimesCount}
              </StatValue>
            </StatCard>
            <StatCard theme={theme}>
              <StatLabel theme={theme}>
                Last Discovery
              </StatLabel>
              <StatValue
                theme={theme}
                style={{ fontSize: "14px" }}
              >
                {discoveryStatus.lastDiscoveryAt
                  ? new Date(
                      discoveryStatus.lastDiscoveryAt
                    ).toLocaleString()
                  : "N/A"}
              </StatValue>
            </StatCard>
          </StatsGrid>
        )}

        {loading ? (
          <Card
            theme={theme}
            style={{ padding: "40px", textAlign: "center" }}
          >
            Yüklənir...
          </Card>
        ) : error ? (
          <Card
            theme={theme}
            style={{
              padding: "40px",
              textAlign: "center",
              color: "#ef4444",
            }}
          >
            Xəta: {error}
          </Card>
        ) : scheduledJobs.length === 0 ? (
          <Card
            theme={theme}
            style={{ padding: "40px", textAlign: "center" }}
          >
            Scheduled notification job-ları tapılmadı
          </Card>
        ) : (
          scheduledJobs.map((scheduleJob) => (
            <ScheduleTimeCard
              key={scheduleJob.scheduleTime}
              theme={theme}
            >
              <ScheduleTimeHeader theme={theme}>
                <ScheduleTimeTitle theme={theme}>
                  <Schedule />
                  {scheduleJob.scheduleTime}
                  <NotificationBadge theme={theme}>
                    {scheduleJob.notificationCount}{" "}
                    notification
                  </NotificationBadge>
                  {scheduleJob.job && (
                    <JobStatusBadge
                      status={scheduleJob.job.status}
                    />
                  )}
                </ScheduleTimeTitle>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      color:
                        getTheme(isDark).colors
                          .textSecondary,
                    }}
                  >
                    Cron: {scheduleJob.cronPattern}
                  </span>
                  {scheduleJob.job && (
                    <>
                      <span
                        style={{
                          fontSize: "12px",
                          color:
                            getTheme(isDark).colors
                              .textSecondary,
                        }}
                      >
                        Next:{" "}
                        {formatDate(
                          scheduleJob.job.nextRunAt
                        )}
                      </span>
                      <span
                        style={{
                          fontSize: "12px",
                          color:
                            getTheme(isDark).colors
                              .textSecondary,
                        }}
                      >
                        Last:{" "}
                        {formatDate(
                          scheduleJob.job.lastRunAt
                        )}
                      </span>
                    </>
                  )}
                  <Button
                    onClick={() =>
                      handleRunWithSimulatedTime(
                        scheduleJob.scheduleTime
                      )
                    }
                    theme={theme}
                    variant="outline"
                    style={{
                      padding: "4px 8px",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <AccessTime fontSize="small" />
                    Simulated Time ilə İşə Sal
                  </Button>
                </div>
              </ScheduleTimeHeader>

              {scheduleJob.notifications.length > 0 && (
                <div
                  style={{
                    padding: getTheme(isDark).spacing.md,
                  }}
                >
                  <Table theme={theme}>
                    <TableHead theme={theme}>
                      <TableRow theme={theme}>
                        <TableHeader theme={theme}>
                          Notification Key
                        </TableHeader>
                        <TableHeader theme={theme}>
                          Title
                        </TableHeader>
                        <TableHeader theme={theme}>
                          Funnel
                        </TableHeader>
                        <TableHeader theme={theme}>
                          Recurring
                        </TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scheduleJob.notifications.map(
                        (notif, idx) => (
                          <TableRow key={idx} theme={theme}>
                            <TableCell theme={theme}>
                              <code
                                style={{ fontSize: "12px" }}
                              >
                                {notif.key}
                              </code>
                            </TableCell>
                            <TableCell theme={theme}>
                              {typeof notif.title ===
                              "object"
                                ? notif.title.az ||
                                  notif.title.en ||
                                  notif.title.ru ||
                                  "N/A"
                                : notif.title || "N/A"}
                            </TableCell>
                            <TableCell theme={theme}>
                              {notif.triggers.map(
                                (trigger, tIdx) => (
                                  <div
                                    key={tIdx}
                                    style={{
                                      fontSize: "12px",
                                    }}
                                  >
                                    {trigger.funnelId
                                      ? `Funnel: ${trigger.funnelId}`
                                      : "N/A"}
                                  </div>
                                )
                              )}
                            </TableCell>
                            <TableCell theme={theme}>
                              {notif.triggers[0]
                                ?.isRecurring !== false
                                ? "Yes"
                                : "No"}
                            </TableCell>
                          </TableRow>
                        )
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}

              {!scheduleJob.job && (
                <div
                  style={{
                    padding: getTheme(isDark).spacing.md,
                    color: "#f59e0b",
                    textAlign: "center",
                  }}
                >
                  ⚠️ Job yaradılmayıb - notification-lar
                  tapılsa da job aktiv deyil
                </div>
              )}
            </ScheduleTimeCard>
          ))
        )}

        {showSimulatedTimeModal && (
          <Card
            theme={theme}
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1000,
              padding: getTheme(isDark).spacing.lg,
              minWidth: "400px",
              maxWidth: "90vw",
            }}
          >
            <h2
              style={{
                marginTop: 0,
                marginBottom: getTheme(isDark).spacing.md,
                color: getTheme(isDark).colors.text,
              }}
            >
              Simulated Time ilə İşə Sal
            </h2>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: getTheme(isDark).spacing.md,
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom:
                      getTheme(isDark).spacing.xs,
                    color: getTheme(isDark).colors.text,
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  Schedule Time:
                </label>
                <input
                  type="text"
                  value={selectedScheduleTime}
                  disabled
                  style={{
                    width: "100%",
                    padding: getTheme(isDark).spacing.sm,
                    border: `1px solid ${
                      getTheme(isDark).colors.border
                    }`,
                    borderRadius:
                      getTheme(isDark).borderRadius.md,
                    backgroundColor:
                      getTheme(isDark).colors.surface,
                    color: getTheme(isDark).colors.text,
                    fontSize: "14px",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom:
                      getTheme(isDark).spacing.xs,
                    color: getTheme(isDark).colors.text,
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  Simulated Time (HH:mm və ya ISO datetime):
                </label>
                <input
                  type="text"
                  value={simulatedTime}
                  onChange={(e) =>
                    setSimulatedTime(e.target.value)
                  }
                  placeholder="19:00 və ya 2024-01-15T19:00:00"
                  style={{
                    width: "100%",
                    padding: getTheme(isDark).spacing.sm,
                    border: `1px solid ${
                      getTheme(isDark).colors.border
                    }`,
                    borderRadius:
                      getTheme(isDark).borderRadius.md,
                    backgroundColor:
                      getTheme(isDark).colors.surface,
                    color: getTheme(isDark).colors.text,
                    fontSize: "14px",
                  }}
                />
                <div
                  style={{
                    marginTop: getTheme(isDark).spacing.xs,
                    fontSize: "12px",
                    color:
                      getTheme(isDark).colors.textSecondary,
                  }}
                >
                  Nümunə: 19:00 və ya 2024-01-15T19:00:00
                </div>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom:
                      getTheme(isDark).spacing.xs,
                    color: getTheme(isDark).colors.text,
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  Timezone (İstəyə bağlı):
                </label>
                <select
                  value={simulatedTimezone}
                  onChange={(e) =>
                    setSimulatedTimezone(e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: getTheme(isDark).spacing.sm,
                    border: `1px solid ${
                      getTheme(isDark).colors.border
                    }`,
                    borderRadius:
                      getTheme(isDark).borderRadius.md,
                    backgroundColor:
                      getTheme(isDark).colors.surface,
                    color: getTheme(isDark).colors.text,
                    fontSize: "14px",
                  }}
                >
                  <option value="">
                    Server Timezone (Default)
                  </option>
                  <option value="Asia/Baku">
                    Asia/Baku (Bakı)
                  </option>
                  <option value="America/New_York">
                    America/New_York (New York)
                  </option>
                  <option value="Europe/London">
                    Europe/London (London)
                  </option>
                  <option value="Asia/Dubai">
                    Asia/Dubai (Dubai)
                  </option>
                  <option value="Europe/Istanbul">
                    Europe/Istanbul (İstanbul)
                  </option>
                  <option value="Asia/Tbilisi">
                    Asia/Tbilisi (Tbilisi)
                  </option>
                  <option value="Asia/Almaty">
                    Asia/Almaty (Almatı)
                  </option>
                  <option value="Europe/Moscow">
                    Europe/Moscow (Moskva)
                  </option>
                  <option value="Asia/Tashkent">
                    Asia/Tashkent (Daşkənd)
                  </option>
                  <option value="Asia/Tehran">
                    Asia/Tehran (Tehran)
                  </option>
                  <option value="Asia/Kolkata">
                    Asia/Kolkata (Kolkata)
                  </option>
                  <option value="Asia/Tokyo">
                    Asia/Tokyo (Tokyo)
                  </option>
                  <option value="Europe/Paris">
                    Europe/Paris (Paris)
                  </option>
                  <option value="America/Los_Angeles">
                    America/Los_Angeles (Los Angeles)
                  </option>
                  <option value="America/Chicago">
                    America/Chicago (Chicago)
                  </option>
                  <option value="Australia/Sydney">
                    Australia/Sydney (Sydney)
                  </option>
                </select>
                <div
                  style={{
                    marginTop: getTheme(isDark).spacing.xs,
                    fontSize: "12px",
                    color:
                      getTheme(isDark).colors.textSecondary,
                  }}
                >
                  Simulated time seçilən timezone-da
                  simulyasiya olunacaq
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: getTheme(isDark).spacing.sm,
                  justifyContent: "flex-end",
                  marginTop: getTheme(isDark).spacing.md,
                }}
              >
                <Button
                  onClick={() => {
                    setShowSimulatedTimeModal(false);
                    setSimulatedTime("");
                    setSimulatedTimezone("");
                    setSelectedScheduleTime(null);
                  }}
                  theme={theme}
                  variant="outline"
                  disabled={running}
                >
                  Ləğv et
                </Button>
                <Button
                  onClick={handleExecuteSimulatedTime}
                  theme={theme}
                  disabled={running}
                >
                  {running ? "İşləyir..." : "İşə Sal"}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {showSimulatedTimeModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 999,
            }}
            onClick={() => {
              if (!running) {
                setShowSimulatedTimeModal(false);
                setSimulatedTime("");
                setSelectedScheduleTime(null);
              }
            }}
          />
        )}
      </PageContainer>
    </MainLayout>
  );
};

export default ScheduledNotificationsView;





